// React
import { useEffect, useState } from "react";
// React Query
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
// MUI
import { Divider, Stack } from "@mui/material";

// API
import { getProviderList, TGetProviderList } from "@Settings/api/AdministrationTools/queries";
import {
	loginKMSProvider,
	logoutKMSProvider,
	TLoginKMSProvider,
	TLogoutKMSProvider,
	TUpdateKMSStoreAndKioskConfigurationInputArgs,
	updateAdministrationTools,
	updateKMSStoreAndKioskConfiguration,
} from "@Settings/api/AdministrationTools/mutations";
import {
	onLoginKMSProvider,
	onLogoutKMSProvider,
	onUpdateKMSStoreAndKioskConfiguration,
} from "@Settings/api/AdministrationTools/subscriptions";

// Context
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";

// Hooks
import { useLocationId } from "@Common/hooks/Location/useLocationId";

// Helpers
import { and, or } from "@Common/helpers/sonar";
import { getSelectedProvider, hasSomeRoleLoggedIn } from "@Settings/helpers/AdministrationTools/Provider/utils";

// Components
import LoggedRoles from "@Settings/Layouts/LoggedRoles";
import ConfigurationSets from "@Settings/components/AdministrationTools/KeyManagementSystem/sections/ConfigurationSets";
import ProviderSection from "@Settings/Layouts/ProviderSection";
import KMSUserEdit from "@Settings/components/AdministrationTools/KeyManagementSystem/sections/KMSUserEdit";

// Types
import { IAdministrationTools, IKmsStore } from "@Settings/types/administrationTools/model";
import { TSelectValue } from "@Layouts/components/InputComponents/SmartSelector/interfaces";
import { GraphqlSubscriptionResponse } from "@Common/api/types/GraphqlSubscriptionResponse";
import { CustomCredentials, KMSSelectedProvider, TSelectedProvider } from "@Settings/types/administrationTools/utils";

type KeyManagementSystemProps = {
	isExpanded: boolean;
	administrationTools: IAdministrationTools;
};

export default function KeyManagementSystem(props: Readonly<KeyManagementSystemProps>) {
	const queryClient = useQueryClient();
	const { addSubscription, deleteSubscription, getSubscription } = useSubscriptions();

	const locationId = useLocationId();

	const keyManagementSystem = props.administrationTools?.integrations?.keyManagementSystem;

	// Local States
	const [isOpen, setIsOpen] = useState(false);
	const [selectedProvider, setSelectedProvider] = useState<Partial<TSelectedProvider> | null>(null);

	/***************************************** QUERIES *****************************************/
	const { data: providers, isLoading: isLoadingProviders } = useQuery({
		queryKey: ["keyManagementProviders", locationId],
		queryFn: async () => getProviderList(locationId as string, "KMS"),
		enabled: Boolean(locationId) && props.isExpanded,
		staleTime: Infinity,
	});

	const updatedSelectedProvider = getSelectedProvider<KMSSelectedProvider>({
		selectedProvider,
		providers: (providers ?? []) as TGetProviderList[],
		adminToolsProvider: keyManagementSystem,
	});

	/***************************************** MUTATIONS *****************************************/
	const { mutate: mutateKeyManagementSystem, isPending: isMutatingKMSProvider } = useMutation({
		mutationKey: ["updateKeyManagementSystem", locationId],
		mutationFn: async (input: Partial<IAdministrationTools>) => updateAdministrationTools(locationId as string, input),
	});

	const { mutate: mutateStoresAndKiosks, isPending: isSavingStoresAndKiosks } = useMutation({
		mutationKey: ["updateStoresAndKiosks", locationId],
		mutationFn: async (input: TUpdateKMSStoreAndKioskConfigurationInputArgs) =>
			updateKMSStoreAndKioskConfiguration(locationId as string, input),
	});

	const {
		data: kmsLoginData,
		mutate: mutateLoginKMSProvider,
		isPending: isKMSSigningIn,
		isError: loginKMSError,
		error: loginKMSErrorData,
		reset: resetLoginKMSError,
		isSuccess: isLoginKMSSuccess,
	} = useMutation({
		mutationKey: ["loginKMSProvider", locationId],
		mutationFn: async (data: TLoginKMSProvider) => {
			const result = await loginKMSProvider(data);
			if (result === null) {
				return Promise.reject(new Error("Login failed, invalid credentials or role not allowed"));
			}
			return result;
		},
	});

	const {
		data: kmsLogoutData,
		mutate: mutateLogoutKMSProvider,
		isPending: isKMSLogoutSigningIn,
		isError: logoutKMSError,
		error: logoutKMSErrorData,
		reset: resetLogoutKMSError,
		isSuccess: isLogoutKMSSuccess,
	} = useMutation({
		mutationKey: ["logoutKMSProvider", locationId],
		mutationFn: async (input: TLogoutKMSProvider) => {
			const result = await logoutKMSProvider(input);
			if (result === null) {
				return Promise.reject(new Error("Login failed"));
			}
			return result;
		},
	});

	/********************************************* SUBSCRIPTIONS ************************************************/
	const keyManagementSystemProviderSubscription = getSubscription(["keyManagementSystemProviderLogin", locationId]);

	// onLoginKMSProvider
	keyManagementSystemProviderSubscription?.subscriptions?.onLoginKMSProvider?.setCallback(
		(result: GraphqlSubscriptionResponse<"onLoginKMSProvider", IAdministrationTools>) => {
			const updatedAdministrationTools = result?.data?.onLoginKMSProvider;

			queryClient.setQueryData(["administrationTools", locationId], () => updatedAdministrationTools);
		}
	);

	// onLogoutKMSProvider
	keyManagementSystemProviderSubscription?.subscriptions?.onLogoutKMSProvider?.setCallback(
		(result: GraphqlSubscriptionResponse<"onLogoutKMSProvider", IAdministrationTools>) => {
			const updatedAdministrationTools = result?.data?.onLogoutKMSProvider;

			queryClient.setQueryData(["administrationTools", locationId], () => updatedAdministrationTools);
		}
	);

	// onUpdateKMSStoreAndKioskConfiguration
	keyManagementSystemProviderSubscription?.subscriptions?.onUpdateKMSStoreAndKioskConfiguration?.setCallback(
		(result: GraphqlSubscriptionResponse<"onUpdateKMSStoreAndKioskConfiguration", IAdministrationTools>) => {
			const updatedAdministrationTools = result?.data?.onUpdateKMSStoreAndKioskConfiguration;

			queryClient.setQueryData(["administrationTools", locationId], () => updatedAdministrationTools);
		}
	);

	/***************************************** HANDLERS *****************************************/
	const handleLogin = () => {
		setIsOpen(true);
	};

	const handleCloseDialog = () => {
		setIsOpen(false);
		resetLoginKMSError();
		resetLogoutKMSError();
	};

	const handleChangedProvider = (provider: Partial<TSelectedProvider>) => {
		setSelectedProvider(provider);
	};

	const handleLoginKMS = (data: CustomCredentials) => {
		const { provider, providerName, providerDBProperty, role } = updatedSelectedProvider ?? {};
		const operation = { role };
		const credentials = { ...data };
		mutateLoginKMSProvider({ locationId, provider, providerName, providerDBProperty, operation, credentials } as TLoginKMSProvider);
	};

	const handleLogoutKMS = () => {
		const { provider, providerDBProperty, role } = updatedSelectedProvider ?? {};
		const operation = { role };

		mutateLogoutKMSProvider({
			locationId: locationId as string,
			provider,
			providerDBProperty,
			operation,
		} as TLogoutKMSProvider);
	};

	// Store and Kiosk Configuration (Section 2)
	const handleUpdateStoreAndKioskConfiguration = (kmsStores: IKmsStore[]) => {
		const { provider, providerDBProperty } = updatedSelectedProvider ?? {};
		const input = {
			provider,
			providerDBProperty,
			kmsStores,
		};
		mutateStoresAndKiosks(input as TUpdateKMSStoreAndKioskConfigurationInputArgs);
	};

	// Allowable Time Away (Section 3)
	const handleUpdateTimeAwayAndChargebackAmount = (
		value: string | TSelectValue,
		property: "timeAway" | "unit" | "chargebackAmount",
		providerDBProperty: string
	) => {
		const providerObj = {
			...keyManagementSystem?.[providerDBProperty as keyof typeof keyManagementSystem],
		};

		if (property === "chargebackAmount") {
			providerObj.chargebackAmount = Number(value);
		} else {
			const propertyValue = property === "timeAway" ? Number(value) : (value as { _id: string; value: string })?._id;

			providerObj.allowableTimeAway = {
				...keyManagementSystem?.[providerDBProperty as keyof typeof keyManagementSystem]?.allowableTimeAway,
				[property]: propertyValue,
			};
		}

		const input: Partial<IAdministrationTools> = {
			integrations: {
				...props.administrationTools?.integrations,
				keyManagementSystem: {
					...keyManagementSystem,
					[providerDBProperty]: providerObj,
				},
			},
		};

		mutateKeyManagementSystem(input);
	};

	useEffect(() => {
		if (locationId) {
			addSubscription(
				["keyManagementSystemProviderLogin", locationId],
				[
					{
						context: "onLoginKMSProvider",
						query: onLoginKMSProvider,
						variables: {
							locationId,
						},
					},
					{
						context: "onLogoutKMSProvider",
						query: onLogoutKMSProvider,
						variables: {
							locationId,
						},
					},
					{
						context: "onUpdateKMSStoreAndKioskConfiguration",
						query: onUpdateKMSStoreAndKioskConfiguration,
						variables: {
							locationId,
						},
					},
				]
			);
		}

		return () => {
			deleteSubscription(["keyManagementSystemProviderLogin", locationId]);
		};
	}, [locationId]);

	const isLoginSuccess = or(isLoginKMSSuccess, isLogoutKMSSuccess);
	const isLoading = or(isKMSSigningIn, isKMSLogoutSigningIn);
	const loginError = or(loginKMSError, logoutKMSError);
	const loginErrorData = loginKMSErrorData || logoutKMSErrorData;

	useEffect(() => {
		// This condition is to ensure to close the dialog when the user logs in or logs out successfully, and there is no error
		if (and(isLoginSuccess, !loginErrorData, Boolean(kmsLoginData), Boolean(kmsLogoutData))) {
			setIsOpen(false);
		}
	}, [isLoginSuccess, loginErrorData, kmsLoginData, kmsLogoutData]);

	return (
		<Stack component={"article"}>
			<ProviderSection
				isOpenDialog={isOpen}
				isSigningIn={isLoading}
				isLoginSuccess={isLoginSuccess}
				selectedProvider={updatedSelectedProvider}
				isMutatingProvider={isMutatingKMSProvider}
				isLoadingProviders={isLoadingProviders}
				resetLoginError={resetLoginKMSError || resetLogoutKMSError}
				loginError={loginError}
				loginErrorData={loginErrorData}
				providers={(providers ?? []) as TGetProviderList[]}
				// Callbacks
				onCloseDialog={handleCloseDialog}
				onLogin={handleLoginKMS}
				onLogout={handleLogoutKMS}
				onChangeProvider={handleChangedProvider}
				onClickProviderLogin={handleLogin}
			/>

			<LoggedRoles selectedProvider={updatedSelectedProvider} />

			{hasSomeRoleLoggedIn(updatedSelectedProvider) && (
				<ConfigurationSets
					isLoading={or(isKMSSigningIn, isKMSLogoutSigningIn)}
					isSavingStoresAndKiosks={isSavingStoresAndKiosks}
					selectedProvider={updatedSelectedProvider as KMSSelectedProvider}
					// Callbacks
					handleUpdateTimeAwayAndChargebackAmount={handleUpdateTimeAwayAndChargebackAmount}
					onUpdateStoreAndKioskConfiguration={handleUpdateStoreAndKioskConfiguration}
				/>
			)}
			<Divider className={"!mt-8 !mb-8"} />
			<KMSUserEdit locationId={locationId} />
		</Stack>
	);
}
